import { HStack, Portal, VStack, Button, Center } from "@chakra-ui/react"

import { Dialog as ChakraDialog } from "@chakra-ui/react"
import { ReactNode, RefObject, useState } from "react"
import { BackButton, CloseButton } from "../content/components/Button"
import { ActionContainer } from "./custom/FormContainer"

interface DialogProps {
  title: string
  description?: string
  children: ReactNode
  trigger?: ReactNode
  footer?: ReactNode | boolean
  loading?: boolean
  disabled?: boolean
  open?: boolean
  setOpen?: (open: boolean) => void
  container?: RefObject<HTMLElement | null>
  fullscreen?: boolean
  showCloseButton?: boolean // Optional prop to show/hide close button
  showBackButton?: boolean // Optional prop to show/hide back button
  onBack?: () => void
  size?: "sm" | "md" | "lg" | "xl" | "xs" | "cover"
  onClose?: () => void
  // Continue button properties
  showContinueButton?: boolean // Whether to show continue button
  onContinue?: () => void // Action to perform when continue is clicked
  continueLabel?: string // Optional custom label (defaults to "Continue")
  continueDisabled?: boolean // Whether continue button is disabled
  bodyOverflow?: "auto" | "visible"
}

/**
 * Defined at module scope on purpose.
 *
 * When this lived inside Dialog it was a new component type on every render,
 * so React unmounted and remounted the whole dialog subtree each time the
 * parent re-rendered — losing focus and flickering on every keystroke in a
 * dialog field.
 */
const Portalled = ({
  container,
  children,
}: {
  container?: RefObject<HTMLElement | null>
  children: ReactNode
}) => {
  if (container && container.current) {
    return <Portal container={container}>{children}</Portal>
  }
  return <>{children}</>
}

const Dialog = (props: DialogProps) => {
  const {
    title,
    description,
    children,
    trigger,
    loading,
    disabled,
    open,
    setOpen,
    footer,
    container,
    fullscreen,
    showCloseButton = true,
    showBackButton = false,
    onBack,
    size,
    onClose,
    showContinueButton = false,
    onContinue,
    continueLabel = "Continue",
    continueDisabled = false,
    bodyOverflow = "auto",
  } = props
  const [openInternal, setOpenInternal] = useState(false)

  const modifyopen = setOpen ?? setOpenInternal

  const hasFooter = footer || showContinueButton

  return (
    <Portalled container={container}>
      <ChakraDialog.Root
        lazyMount
        open={open ?? openInternal}
        onOpenChange={(e) => modifyopen(e.open)}
        onEscapeKeyDown={() => {
          modifyopen(false)
          onClose?.()
        }}
        closeOnEscape={true}
        closeOnInteractOutside={false}
        size={fullscreen ? "full" : size}
        scrollBehavior="inside"
      >
        {trigger && (
          <ChakraDialog.Trigger disabled={disabled} as="div" width="fit-content">
            {trigger}
          </ChakraDialog.Trigger>
        )}
        <ChakraDialog.Backdrop pointerEvents="auto" />
        <ChakraDialog.Positioner backgroundColor={fullscreen ? "background" : undefined} pointerEvents="auto">
          <ChakraDialog.Content
            height={fullscreen ? "100vh" : size === "cover" ? "90vh" : undefined}
            maxHeight={fullscreen ? undefined : size === "cover" ? "90vh" : "85vh"}
            background="background"
            boxShadow="none"
            borderRadius={fullscreen ? "none" : undefined}
            overflow={fullscreen ? undefined : bodyOverflow === "visible" ? "visible" : "hidden"}
            pointerEvents="auto"
            zIndex={1000}
            display="flex"
            flexDirection="column"
            style={{ cursor: "default" }}
          >
            <ChakraDialog.Header color={fullscreen ? "background" : undefined} bg={fullscreen ? "blue" : undefined}>
              <VStack gap={2} align="stretch">
                <HStack>
                  {showBackButton && onBack ? <BackButton onClick={onBack} disabled={loading} /> : undefined}
                  <ChakraDialog.Title>{title}</ChakraDialog.Title>
                </HStack>
                {description && <ChakraDialog.Description>{description}</ChakraDialog.Description>}
              </VStack>
            </ChakraDialog.Header>
            <ChakraDialog.Body
              mt={fullscreen ? "8px" : undefined}
              pb="16px"
              overflowY={bodyOverflow}
              overflowX={bodyOverflow === "visible" ? "visible" : "hidden"}
              flex="1"
              minHeight={0}
              display={fullscreen ? "flex" : undefined}
              flexDirection={fullscreen ? "column" : undefined}
            >
              <VStack
                height={fullscreen ? undefined : undefined}
                flex={fullscreen ? "1" : undefined}
                alignItems={"flex-start"}
                gap={2}
                width="100%"
              >
                {children}
              </VStack>
            </ChakraDialog.Body>

            {hasFooter && (
              <ActionContainer>
                <HStack width="100%" height="100%">
                  {showContinueButton && onContinue ? (
                    <Button
                      variant="hydrologiq_primary"
                      onClick={onContinue}
                      disabled={continueDisabled || loading}
                      width="100%"
                    >
                      {continueLabel}
                    </Button>
                  ) : (
                    footer
                  )}
                </HStack>
              </ActionContainer>
            )}
            {showCloseButton && (
              <ChakraDialog.CloseTrigger
                zIndex={1000}
                onClick={() => {
                  modifyopen(false)
                  onClose?.()
                }}
                disabled={loading}
                asChild
                mt={fullscreen ? "12px" : undefined}
              >
                <CloseButton />
              </ChakraDialog.CloseTrigger>
            )}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </ChakraDialog.Root>
    </Portalled>
  )
}

export default Dialog
